import React, { useState, useEffect } from 'react';
import { Fade, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

function AnimatedFunnelBar({ stage, index, classes }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    const [animatedWidth, setAnimatedWidth] = useState(0);
    const [animatedCount, setAnimatedCount] = useState(0);
    const [animatedPercentage, setAnimatedPercentage] = useState(0);
    const [animatedDropOff, setAnimatedDropOff] = useState(0);
    const [animatedDropOffCount, setAnimatedDropOffCount] = useState(0);

    // Define minimum width percentage based on screen size
    const getMinimumWidth = () => {
        if (isMobile) return 0; // No minimum width on mobile, let it be natural
        if (isTablet) return 10; // Smaller minimum on tablets
        return 15; // Desktop minimum
    };

    const MINIMUM_WIDTH_PERCENT = getMinimumWidth();

    useEffect(() => {
        // Delay animation start based on index
        const delayTimer = setTimeout(() => {
            const duration = 1000;
            const steps = 60;
            const stepDuration = duration / steps;

            // Calculate target width (use minimum if percentage is too small)
            const targetWidth = Math.max(stage.percentage, MINIMUM_WIDTH_PERCENT);

            // Animate width
            const widthIncrement = targetWidth / steps;
            let currentWidthStep = 0;

            const widthTimer = setInterval(() => {
                currentWidthStep++;
                if (currentWidthStep >= steps) {
                    setAnimatedWidth(targetWidth);
                    clearInterval(widthTimer);
                } else {
                    setAnimatedWidth(widthIncrement * currentWidthStep);
                }
            }, stepDuration);

            // Animate count
            const countIncrement = stage.count / steps;
            let currentCountStep = 0;

            const countTimer = setInterval(() => {
                currentCountStep++;
                if (currentCountStep >= steps) {
                    setAnimatedCount(stage.count);
                    clearInterval(countTimer);
                } else {
                    setAnimatedCount(Math.floor(countIncrement * currentCountStep));
                }
            }, stepDuration);

            // Animate percentage
            const percentageIncrement = stage.percentage / steps;
            let currentPercentageStep = 0;

            const percentageTimer = setInterval(() => {
                currentPercentageStep++;
                if (currentPercentageStep >= steps) {
                    setAnimatedPercentage(stage.percentage);
                    clearInterval(percentageTimer);
                } else {
                    setAnimatedPercentage(percentageIncrement * currentPercentageStep);
                }
            }, stepDuration);

            // Animate drop-off if exists
            let dropOffTimer, dropOffCountTimer;
            if (stage.drop_off > 0) {
                const dropOffIncrement = stage.drop_off / steps;
                let currentDropOffStep = 0;

                dropOffTimer = setInterval(() => {
                    currentDropOffStep++;
                    if (currentDropOffStep >= steps) {
                        setAnimatedDropOff(stage.drop_off);
                        clearInterval(dropOffTimer);
                    } else {
                        setAnimatedDropOff(dropOffIncrement * currentDropOffStep);
                    }
                }, stepDuration);

                // Animate drop-off count
                const dropOffCountIncrement = (stage.drop_off_count || 0) / steps;
                let currentDropOffCountStep = 0;

                dropOffCountTimer = setInterval(() => {
                    currentDropOffCountStep++;
                    if (currentDropOffCountStep >= steps) {
                        setAnimatedDropOffCount(stage.drop_off_count || 0);
                        clearInterval(dropOffCountTimer);
                    } else {
                        setAnimatedDropOffCount(Math.floor(dropOffCountIncrement * currentDropOffCountStep));
                    }
                }, stepDuration);
            }

            return () => {
                clearInterval(widthTimer);
                clearInterval(countTimer);
                clearInterval(percentageTimer);
                if (dropOffTimer) clearInterval(dropOffTimer);
                if (dropOffCountTimer) clearInterval(dropOffCountTimer);
            };
        }, index * 150);

        return () => clearTimeout(delayTimer);
    }, [stage, index, MINIMUM_WIDTH_PERCENT]);

    const formatNumber = (num) => {
        return Math.floor(num).toLocaleString();
    };

    // Adjust layout for mobile
    const barStyle = {
        width: `${animatedWidth}%`,
        minWidth: isMobile ? 'auto' : undefined,
        padding: isMobile ? theme.spacing(0, 1.5) : theme.spacing(0, 2.5),
        fontSize: isMobile ? '0.85rem' : undefined,
    };

    return (
        <Fade in={true} timeout={500 + (index * 100)}>
            <div className={classes.funnelStage}>
                <div className={classes.funnelBarContainer}>
                    <div
                        className={classes.funnelBar}
                        style={barStyle}
                    >
                        <span className={classes.funnelLabel}>
                            {stage.stage}
                        </span>
                        {!isMobile && (
                            <span className={classes.funnelCount}>
                                {formatNumber(animatedCount)}
                            </span>
                        )}
                    </div>
                </div>
                <div className={classes.funnelInfo}>
                    <div className={classes.funnelPercentage}>
                        {animatedPercentage.toFixed(2)}%
                    </div>
                    {isMobile && (
                        <div style={{ fontSize: '0.8rem', marginTop: '2px', color: theme.palette.text.secondary }}>
                            {formatNumber(animatedCount)}
                        </div>
                    )}
                    {stage.drop_off > 0 && (
                        <div className={classes.funnelDropoff} style={{ fontSize: isMobile ? '0.75rem' : undefined }}>
                            ↓ {animatedDropOff.toFixed(2)}% drop-off
                            {!isMobile && (
                                <>
                                    <br />
                                    ({formatNumber(animatedDropOffCount)} messages)
                                </>
                            )}
                            {stage.drop_off > 70 && (
                                <span className={classes.criticalText}> - Critical!</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Fade>
    );
}

export default AnimatedFunnelBar;